import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppToast } from "@/hooks/useAppToast";
import {
  getGroup,
  updateGroup,
  deleteGroup,
  addWordToVocabularyGroup,
  removeWordFromVocabularyGroup,
  updateWordInVocabularyGroup
} from "@/services/admin/vocabularyGroups.service";
import {
  Group,
  UpdateVocabularyGroupInputs,
  VocabularyWord
} from "@/services/admin/vocabularyGroups.interfaces";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash, PlusCircle, Edit } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/data-table";

const AdminEditVocabularyGroupPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const toast = useAppToast();

  const [isLoading, setIsLoading] = useState(true);
  const [groupData, setGroupData] = useState<Group | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [wordToDelete, setWordToDelete] = useState<string | null>(null);
  const [newWord, setNewWord] = useState({ word: "", translation: "", exampleSentence: "" });
  const [editWord, setEditWord] = useState<VocabularyWord>({ id: "", word: "", translation: "", exampleSentence: "" });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('word');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    const loadGroup = async () => {
      if (!groupId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getGroup(groupId);
        setGroupData(data);
      } catch (error) {
        console.error("Error loading vocabulary group:", error);
        toast.error(error instanceof Error ? error.message : "Failed to load vocabulary group data");
        navigate("/admin/educational/vocabulary-management");
      } finally {
        setIsLoading(false);
      }
    };

    loadGroup();
  }, [groupId]);

  const handleSave = async (changes: UpdateVocabularyGroupInputs) => {
    if (!groupId) return;

    try {
      await updateGroup(groupId, changes);
      setGroupData((prev: Group | null) => (prev ? { ...prev, ...changes } : null));
      toast.success("Group updated successfully");
    } catch (error) {
      console.error("Error updating vocabulary group:", error);
      throw error;
    }
  };

  const handleAddWord = async () => {
    if (!groupId) return;

    try {
      await toast.promise(addWordToVocabularyGroup(groupId, newWord),
        {
          loading: "Adicionando palavra...",
          success: "Palavra adicionada com sucesso",
          error: "Erro ao adicionar palavra"
        }
      );
      const updatedGroup = await getGroup(groupId);
      setGroupData(updatedGroup);
      setNewWord({ word: "", translation: "", exampleSentence: "" });
    } catch (error) {
      console.error("Error adding word:", error);
    }
  }

  const handleRemoveWord = async (wordId: string) => {
    setWordToDelete(wordId);
    setDeleteDialogOpen(true);
  }

  const confirmDeleteWord = async () => {
    if (!groupId || !wordToDelete) return;

    setIsDeleting(true);

    try {
      await toast.promise(removeWordFromVocabularyGroup(groupId, wordToDelete),
        {
          loading: "Removendo palavra...",
          success: "Palavra removida com sucesso",
          error: "Erro ao remover palavra"
        });

      const updatedGroup = await getGroup(groupId);
      setGroupData(updatedGroup);
      setDeleteDialogOpen(false);
      setWordToDelete(null);

    } catch (error) {
      console.error("Error removing word:", error);
    } finally {
      setIsDeleting(false);
    }
  }

  const handleUpdateWord = async () => {
    if (!groupId || !editWord.id) return;
    try {
      await toast.promise(updateWordInVocabularyGroup(groupId, editWord.id, {
        word: editWord.word,
        translation: editWord.translation,
        exampleSentence: editWord.exampleSentence
      }),
      {
        loading: "Atualizando palavra...",
        success: "Palavra atualizada com sucesso",
        error: "Erro ao atualizar palavra"
      });

      const updatedGroup = await getGroup(groupId);
      setGroupData(updatedGroup);
      setEditDialogOpen(false);
      setEditWord({ id: "", word: "", translation: "", exampleSentence: "" });

    } catch (error) {
      console.error("Error updating word:", error);
    }
  }

  const handleDelete = async () => {
    if (!groupId) return;

    const confirmed = window.confirm(
      "Tem certeza que deseja excluir este grupo de vocabulário?"
    );
    if (!confirmed) return;

    try {
      await toast.promise(deleteGroup([groupId]), {
        loading: "Deleting group...",
        success: "Group deleted successfully",
        error: "Failed to delete group",
      });
      navigate("/admin/educational/vocabulary-management");
    } catch (error) {
      console.error("Error deleting vocabulary group:", error);
    }
  };

  if (isLoading) {
    return (
      <AdminPageLayout title="Loading Vocabulary Group...">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-neutral-600">Loading vocabulary group data...</p>
        </div>
      </AdminPageLayout>
    );
  }

  if (!groupData) {
    return (
      <AdminPageLayout title="Vocabulary Group Not Found">
        <div className="flex items-center justify-center py-8">
          Vocabulary group not found
        </div>
      </AdminPageLayout>
    );
  }

  const tableHeaders = [
    { key: 'word', label: 'Palavra' },
    { key: 'translation', label: 'Tradução' },
    { key: 'exampleSentence', label: 'Exemplo' },
    { key: 'actions', label: 'Ações' }
  ];

  const filteredAndSortedWords = [...groupData.words]
    .filter(word => {
      const search = searchTerm.toLowerCase();
      return (
        (word.word || '').toLowerCase().includes(search) ||
        (word.translation || '').toLowerCase().includes(search) ||
        (word.exampleSentence || '').toLowerCase().includes(search)
      );
    })
    .sort((a: VocabularyWord, b: VocabularyWord) => {
    if (sortColumn === 'actions') return 0;
    const aValue = a[sortColumn as keyof typeof a] || '';
    const bValue = b[sortColumn as keyof typeof b] || '';
    return sortDirection === 'asc'
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });

  return (
    <AdminPageLayout title={`Editar ${groupData.name}`}>
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Palavra</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir esta palavra? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
              aria-label="Cancelar exclusão"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteWord}
              disabled={isDeleting}
              aria-label={isDeleting ? "Excluindo palavra..." : "Confirmar exclusão"}
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" role="status" aria-label="Carregando">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Excluindo...
                </>
              ) : (
                "Excluir"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Palavra</DialogTitle>
            <DialogDescription>
              Edite os detalhes da palavra abaixo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label htmlFor="edit-word" className="block text-sm font-medium mb-1">Palavra</label>
              <Input
                id="edit-word"
                value={editWord.word}
                onChange={(e) => setEditWord({ ...editWord, word: e.target.value })}
                aria-label="Palavra em inglês"
              />
            </div>
            <div>
              <label htmlFor="edit-translation" className="block text-sm font-medium mb-1">Tradução</label>
              <Input
                id="edit-translation"
                value={editWord.translation}
                onChange={(e) => setEditWord({ ...editWord, translation: e.target.value })}
                aria-label="Tradução em português"
              />
            </div>
            <div>
              <label htmlFor="edit-example" className="block text-sm font-medium mb-1">Frase de Exemplo</label>
              <Input
                id="edit-example"
                value={editWord.exampleSentence}
                onChange={(e) => setEditWord({ ...editWord, exampleSentence: e.target.value })}
                aria-label="Frase de exemplo em inglês"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              aria-label="Cancelar edição"
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              onClick={handleUpdateWord}
              aria-label="Salvar alterações"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mb-4">
        <label htmlFor="groupName" className="block text-sm font-medium text-gray-700">Nome do Grupo</label>
        <Input
          id="groupName"
          defaultValue={groupData.name}
          onBlur={(e) => handleSave({ name: e.target.value })}
          className="mt-1"
          aria-label="Nome do grupo de vocabulário"
        />
      </div>
      <div className="mb-4">
        <label htmlFor="groupDescription" className="block text-sm font-medium text-gray-700">Descrição do Grupo</label>
        <Textarea
          id="groupDescription"
          defaultValue={groupData.description}
          onBlur={(e) => handleSave({ description: e.target.value })}
          className="mt-1"
          aria-label="Descrição do grupo de vocabulário"
        />
      </div>

      <div className="mb-4 space-y-4">
        <label className="block text-sm font-medium text-gray-700">Adicionar Nova Palavra</label>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Input
              id="new-word"
              placeholder="Palavra"
              value={newWord.word}
              onChange={(e) => setNewWord({ ...newWord, word: e.target.value })}
              aria-label="Nova palavra em inglês"
            />
          </div>
          <div>
            <Input
              id="new-translation"
              placeholder="Tradução"
              value={newWord.translation}
              onChange={(e) => setNewWord({ ...newWord, translation: e.target.value })}
              aria-label="Tradução em português"
            />
          </div>
          <div>
            <Input
              id="new-example"
              placeholder="Frase de Exemplo"
              value={newWord.exampleSentence}
              onChange={(e) => setNewWord({ ...newWord, exampleSentence: e.target.value })}
              aria-label="Frase de exemplo em inglês"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleAddWord}
            aria-label="Adicionar nova palavra ao grupo"
          >
            <PlusCircle className="h-4 w-4 mr-2" /> Adicionar Palavra
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center space-x-2">
          <Input
            type="text"
            placeholder="Buscar palavras..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
            aria-label="Buscar palavras por texto"
          />
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            {tableHeaders.map(header => (
              <TableHead
                key={header.key}
                className={`${header.key === 'actions' ? 'w-[100px]' : ''} cursor-pointer hover:bg-gray-50`}
                onClick={() => header.key !== 'actions' && handleSort(header.key)}
                role="columnheader"
                aria-sort={
                  sortColumn === header.key
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                <div className="flex items-center gap-1">
                  {header.label}
                  {header.key !== 'actions' && (
                    <span className="text-gray-400">
                      {sortColumn === header.key ? (
                        sortDirection === 'asc' ? '↑' : '↓'
                      ) : '↕'}
                    </span>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredAndSortedWords.map((word) => (
            <TableRow key={word.id}>
              <TableCell>{word.word}</TableCell>
              <TableCell>{word.translation}</TableCell>
              <TableCell>{word.exampleSentence}</TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditWord(word);
                      setEditDialogOpen(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleRemoveWord(word.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <footer className="flex justify-end items-center border-t pt-6 mt-6">
        <Button
          variant="ghost"
          className="text-neutral-600"
          onClick={handleDelete}
        >
          <Trash className="h-4 w-4 mr-2" /> Excluir Grupo
        </Button>
      </footer>
    </AdminPageLayout>
  );
};

export default AdminEditVocabularyGroupPage;